import difference from 'lodash-es/difference';
import isEqual from 'lodash-es/isEqual';
import uniq from 'lodash-es/uniq';
import { createContext, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { buildBrandSpecialFilter } from '@jane/search/util';
/* eslint-disable-next-line no-restricted-imports */
import type { BrandSpecial } from '@jane/shared/models';
import type { Cart, EmptyCart } from '@jane/shared/types';

import type { RootLoaderType } from '../../../data-access';
import {
  useGetCart,
  useKioskUser,
  useTargetedBrandSpecialsForStore,
} from '../../../data-access';
import {
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '../../../hooks';
import { useEnsureContextWithinProvider } from '../hooks/useEnsureContextWithinProvider';

export interface BrandSpecialsContext {
  addedBrandSpecialIds: number[];
  brandSpecialFilter: string;
  brandSpecials: BrandSpecial[];
  cart: Cart | EmptyCart | null;
  loadingBrandSpecials: boolean;
  removedBrandSpecialIds: number[];
  shouldShowAlert: boolean;
}

export const BrandSpecialsContext = createContext<BrandSpecialsContext | null>(
  null
);

export const BrandSpecialsProvider = ({ children }: PropsWithChildren) => {
  const [cartBrandSpecialIds, setCartBrandSpecialIds] = useState<number[]>([]);
  const [addedBrandSpecialIds, setAddedBrandSpecialIds] = useState<number[]>(
    []
  );
  const [removedBrandSpecialIds, setRemovedBrandSpecialIds] = useState<
    number[]
  >([]);

  const { storeId } = useTypedRouteLoaderData<RootLoaderType>('root');

  const shouldShowGold = useShouldKioskShowGold();

  const { data: kioskUser } = useKioskUser();
  const { data: cart } = useGetCart();
  const {
    data,
    isFetching: isFetchingBrandSpecials,
    refetch: refetchBrandSpecials,
  } = useTargetedBrandSpecialsForStore(storeId, {
    enabled: shouldShowGold,
  });

  const brandSpecials = useMemo(() => data?.brand_specials ?? [], [data]);

  const brandSpecialIds = useMemo(
    () => brandSpecials?.map(({ id }) => id),
    [brandSpecials]
  );

  const brandSpecialFilter = useMemo(
    () => buildBrandSpecialFilter(brandSpecialIds),
    [brandSpecialIds]
  );

  useEffect(() => {
    const filteredIds =
      cart?.products && cart.products.length > 0
        ? cart.products
            .map(({ brand_special_id }) => brand_special_id)
            .filter(
              (id: number | undefined | null) => id !== null && id !== undefined
            )
            .sort((a, b) => (a as number) - (b as number))
        : [];
    const newBrandSpecialIds = uniq<number>(filteredIds as number[]);

    if (!isEqual(cartBrandSpecialIds, newBrandSpecialIds)) {
      const addedIds = difference<number>(
        newBrandSpecialIds,
        cartBrandSpecialIds
      );
      setAddedBrandSpecialIds(addedIds);

      const removedIds = difference<number>(
        cartBrandSpecialIds,
        newBrandSpecialIds
      );
      setRemovedBrandSpecialIds(removedIds);

      setCartBrandSpecialIds(newBrandSpecialIds);
    } else if (
      addedBrandSpecialIds?.length > 0 ||
      removedBrandSpecialIds?.length > 0
    ) {
      setAddedBrandSpecialIds([]);
      setRemovedBrandSpecialIds([]);
    }
  }, [cart, cartBrandSpecialIds, addedBrandSpecialIds, removedBrandSpecialIds]);

  useEffect(() => {
    if (shouldShowGold) {
      refetchBrandSpecials();
    }
  }, [kioskUser]);

  const context = useMemo(
    () => ({
      addedBrandSpecialIds,
      brandSpecialFilter,
      brandSpecials,
      cart: cart ?? null,
      loadingBrandSpecials: isFetchingBrandSpecials,
      removedBrandSpecialIds,
      shouldShowAlert: Boolean(
        cart?.brand_special_has_changed &&
        (addedBrandSpecialIds?.length > 0 || removedBrandSpecialIds?.length > 0)
      ),
    }),
    [
      addedBrandSpecialIds,
      brandSpecialFilter,
      brandSpecials,
      cart,
      isFetchingBrandSpecials,
      removedBrandSpecialIds,
    ]
  );

  return (
    <BrandSpecialsContext.Provider value={context}>
      {children}
    </BrandSpecialsContext.Provider>
  );
};

export const useBrandSpecials = () =>
  useEnsureContextWithinProvider(BrandSpecialsContext, 'BrandSpecials');
